import moment from 'moment';
import { InvestigationSourceTypesMap } from '@usealloy/typegen';

export const INVESTIGATION_STATES = {
  ALERT_REVIEW: 'Alert Review',
  CASE_REVIEW: 'Case Review',
} as const;

export const CHECK_JOURNEY_ALERT_STATUS_ATTEMPTS = 10;
export const CHECK_JOURNEY_ALERT_STATUS_INTERVAL = 1_000;

// Specific Investigation error messages from API that we match against for specific UI messaging
export const CURRENT_AGENT_CLOSE_ERROR = 'Only the current assigned agent can close an Investigation';
export const CURRENT_AGENT_PROMOTE_ERROR = 'Only the current assigned agent can promote an Investigation';
export const INVESTIGATION_OPEN_ALERTS_CLOSE_ERROR = 'Cannot close an Investigation with open Alerts';

// Investigation error messages to display
export const INVESTIGATION_OPEN_ALERTS_CLOSE = 'This Investigation still has open Alerts and cannot be closed';

export const HARDCODED_INV_ERRORS = new Set([
  CURRENT_AGENT_CLOSE_ERROR,
  CURRENT_AGENT_PROMOTE_ERROR,
  INVESTIGATION_OPEN_ALERTS_CLOSE,
  INVESTIGATION_OPEN_ALERTS_CLOSE_ERROR,
]);

export const INVESTIGATION_REVIEW_ALLOWED_OUTCOMES = [
  'Confirmed Risk',
  'Dismissed',
] as const;

export const INVESTIGATION_QUEUE_INITIAL_FILTER_VALUES = {
  createdAtFrom: moment().subtract(30, 'days').toISOString(),
  createdAtTo: moment().endOf('day').toISOString(),
  archived: 'false',
};

export const MANUAL_INVESTIGATION_TYPES = new Set([
  InvestigationSourceTypesMap.MANUAL_AGENT,
  InvestigationSourceTypesMap.MANUAL_API,
]);

export const INVESTIGATION_AUTO_ASSIGN = 'auto-assign';

/**
 * Body of the note the QC sampling flow writes to every Investigation it pulls into a sample.
 *
 * Doubles as the activity-feed label: the feed matches notes on this exact text and renders it in
 * place of "added a note", suppressing the note body. Must stay in sync with frontend-dashboard's
 * constant of the same name, which writes the note.
 */
export const ADDED_T0_QC_REVIEW = 'added Investigation to Quality Control Review';

/**
 * Key wrapping the structured body of the note written when a QC review is submitted.
 *
 * The note text is JSON — `{ [QC_REVIEW_SUBMITTED]: { … } }` — so the activity feed can render the
 * conclusion and checklist rather than matching a bare sentinel string. Question *labels* are
 * stored alongside the answers so an entry stays readable after the checklist in `qcQuestions.ts`
 * changes; resolving ids at render time would silently blank out older reviews.
 */
export const QC_REVIEW_SUBMITTED = 'qc_review_submitted';

export type QcReviewAnswer = {
  answer: 'no' | 'yes';
  label: string;
};

/**
 * Snapshot of the reviewed Investigation and its SAR, taken when the review is submitted.
 *
 * Stored on the note rather than resolved when the feed renders it: a review is a record of what
 * the reviewer saw, so a later amendment or re-filing must not rewrite a completed one. Every
 * field but the Investigation token is optional — the SAR endpoint is flag- and permission-gated,
 * and a review can be submitted before a SAR exists.
 */
export type QcReviewSourceData = {
  /** Agent who closed the Investigation, which for this flow is the agent who filed the SAR. */
  bsaAnalystName?: string;
  caseDispositionDate?: string;
  investigationToken: string;
  sarFiledDate?: string;
  sarReportToken?: string;
  sarReportVersion?: number;
  /**
   * "Initial SAR Deemed Suspicious" / "90 Day/Amended SAR Begin Review" date.
   *
   * Nothing populates this yet: `SarReports` stores only the `is_ninety_day_review` boolean, the
   * `SarWorks` rows that record the flagging are insert-only with no read endpoint, and the
   * Investigation timeline carries no SAR events. The field is part of the note shape so the row
   * can be filled once a source exists, without changing the format of notes already written.
   */
  sarReviewBeginDate?: string;
  /** `Initial`, `Amended`, `Continuing`, or a `/`-joined combination, as the SAR queue shows it. */
  sarType?: string;
};

export type QcReviewSubmission = {
  answers: QcReviewAnswer[];
  conclusion: string;
  note?: string;
  sourceData?: QcReviewSourceData;
};

/** Serializes a submitted QC review into the note body the activity feed recognizes. */
export const buildQcReviewNote = (submission: QcReviewSubmission): string => (
  JSON.stringify({ [QC_REVIEW_SUBMITTED]: submission })
);

/** Returns the submission from a structured QC review note, or null when the text is not one. */
export const parseQcReviewNote = (text: string): QcReviewSubmission | null => {
  try {
    const parsed: unknown = JSON.parse(text);

    if (typeof parsed !== 'object' || parsed === null || !(QC_REVIEW_SUBMITTED in parsed)) {
      return null;
    }

    const submission = (parsed as Record<string, unknown>)[QC_REVIEW_SUBMITTED];

    if (
      typeof submission !== 'object'
      || submission === null
      || typeof (submission as QcReviewSubmission).conclusion !== 'string'
      || !Array.isArray((submission as QcReviewSubmission).answers)
    ) {
      return null;
    }

    return submission as QcReviewSubmission;
  } catch {
    // Plain-text note — not a structured QC review.
    return null;
  }
};

/**
 * Whether a note body is one of the QC flow's own notes — the sample marker or a submitted review.
 *
 * Used to decide QC-sample membership and to build the activity feed's "Quality Control" grouping.
 */
export const isQcReviewNoteText = (text: string): boolean => (
  text === ADDED_T0_QC_REVIEW || parseQcReviewNote(text) !== null
);

export const TAGS_AND_OUTCOME_REASONS_LABEL = 'Tags and Outcome Reasons';

/** Filters that have been replaced with newer keys but may still be present in saved searches */
export const DEPRECATED_FILTER_KEYS = ['tags', 'outcomeReasons'] as const;

export const DEPRECATED_FILTER_MAPPING = {
  tags: TAGS_AND_OUTCOME_REASONS_LABEL,
  outcomeReasons: TAGS_AND_OUTCOME_REASONS_LABEL,
} satisfies Record<(typeof DEPRECATED_FILTER_KEYS)[number], string>;
